import { Service, computed, inject, signal } from '@angular/core';
import { DbService } from './db.service';
import { Sesion } from './sesion.model';

/** Clave compartida con FrontKordata para el arreglo de sesiones. */
const SESIONS_KEY = 'sesions';

/**
 * Maneja las cuentas conectadas en este navegador. Cada posicion del arreglo
 * es el `:id` de la ruta, igual que en FrontKordata.
 */
@Service()
export class MultiSesionService {
  private readonly db = inject(DbService);
  private readonly sesionesState = signal<Sesion[]>(this.leerDeStorage());

  readonly sesiones = this.sesionesState.asReadonly();
  readonly total = computed(() => this.sesionesState().length);

  /** Guarda la sesion y devuelve su indice, reemplazando la del mismo usuario. */
  agregar(sesion: Sesion): number {
    const actuales = this.sesionesState();
    const existente = actuales.findIndex(s => s.idUsuario === sesion.idUsuario);
    const siguientes =
      existente >= 0
        ? actuales.map((s, i) => (i === existente ? sesion : s))
        : [...actuales, sesion];

    this.persistir(siguientes);
    return siguientes.findIndex(s => s.token === sesion.token);
  }

  obtener(indice: number): Sesion | null {
    return this.sesionesState()[indice] ?? null;
  }

  obtenerToken(indice: number): string | null {
    return this.obtener(indice)?.token ?? null;
  }

  eliminar(indice: number): void {
    this.persistir(this.sesionesState().filter((_, i) => i !== indice));
  }

  eliminarTodas(): void {
    this.sesionesState.set([]);
    this.db.delete(SESIONS_KEY);
  }

  /** Relee storage para reflejar cambios hechos en otra pestana. */
  sincronizar(): void {
    this.sesionesState.set(this.leerDeStorage());
  }

  private persistir(sesiones: Sesion[]): void {
    this.sesionesState.set(sesiones);
    this.db.set(SESIONS_KEY, sesiones);
  }

  private leerDeStorage(): Sesion[] {
    const guardadas = this.db.get<Sesion[]>(SESIONS_KEY);
    return Array.isArray(guardadas) ? guardadas.filter(s => !!s?.token) : [];
  }
}
